import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { Role, Gelanggang, BengkungLevel, UserProfile } from '../../types';
import { getMembersDB, saveMemberDB, saveInvoiceDB, getNextMemberId } from '../../services/db';
import { isValidEmail, sanitizeIc, sanitizePhone, toTitleCase } from '../../utils/formatters';
import { createOnboardingInvoice, UNIFORM_SIZES, getUniformPriceBySize } from '../../utils/pricing';
import {
  ShieldCheck,
  UserCheck,
  ArrowRight,
  Shield,
  HeartHandshake,
  Flame,
  ChevronRight,
  User,
  Lock,
  LogIn,
  UserPlus,
  CheckCircle2,
  Award,
  CreditCard,
  Bell,
  BookOpen,
  Link2,
  AlertCircle,
  Calendar,
  Ruler,
  Users,
  Plus,
  Trash2,
  Info
} from 'lucide-react';
import { UniformSizeChartModal } from '../../components/common/UniformSizeChartModal';

interface LoginPageProps {
  onLoginSuccess: () => void;
  initialMode?: 'login' | 'register';
  onModeChange?: (mode: 'login' | 'register') => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, initialMode = 'login', onModeChange }) => {
  const { loginAs, loginWithGoogle, bindGoogleAccount, setCurrentUser } = useAuth();
  const { showToast } = useToast();

  const [mode, setMode] = useState<'login' | 'register'>(initialMode);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isBindingLoading, setIsBindingLoading] = useState(false);

  // State for Google Account Binding prompt modal
  const [pendingGoogleBinding, setPendingGoogleBinding] = useState<{
    googleUser: { uid: string; email: string; displayName?: string | null; photoURL?: string | null };
    existingMember: UserProfile;
  } | null>(null);

  // Login form state
  const [icNo, setIcNo] = useState('');
  const [password, setPassword] = useState('');

  // Registration form state
  const [regRole, setRegRole] = useState<'Ahli' | 'Waris'>('Ahli');
  const [regName, setRegName] = useState('');
  const [regIcNo, setRegIcNo] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regAddress, setRegAddress] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regGelanggang, setRegGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [regUniformSize, setRegUniformSize] = useState<string>('150cm');
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [regIsAlsoPesilat, setRegIsAlsoPesilat] = useState(false);

  // Mandatory pesilat (tanggungan / ahli keluarga) details when registering as Waris
  const [regChildren, setRegChildren] = useState<Array<{
    name: string;
    icNo: string;
    gelanggang: Gelanggang;
    uniformSize: string;
    phone?: string;
    relationship?: string;
  }>>([
    {
      name: '',
      icNo: '',
      gelanggang: 'Pusaka Warisan',
      uniformSize: '130cm',
      phone: '',
      relationship: 'Anak'
    }
  ]);

  const handleAddChild = () => {
    setRegChildren(prev => [
      ...prev,
      {
        name: '',
        icNo: '',
        gelanggang: prev[0]?.gelanggang || 'Pusaka Warisan',
        uniformSize: '130cm',
        phone: '',
        relationship: 'Anak'
      }
    ]);
  };

  const handleRemoveChild = (index: number) => {
    if (regChildren.length <= 1) {
      showToast('warning', 'Wajib Diisi', 'Pendaftaran sebagai Waris wajib menyertakan sekurang-kurangnya seorang pesilat berdaftar.');
      return;
    }
    setRegChildren(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateChild = (index: number, field: string, value: string) => {
    setRegChildren(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Waris pesilat mode: 'register' (new child) vs 'link' (existing child)
  const [regWarisMode, setRegWarisMode] = useState<'register' | 'link'>('register');
  const [regLinkChildren, setRegLinkChildren] = useState<Array<{
    id: string;
    icNo: string;
    relationship: string;
  }>>([
    {
      id: 'link_1',
      icNo: '',
      relationship: 'Anak'
    }
  ]);

  const handleAddLinkChild = () => {
    setRegLinkChildren(prev => [
      ...prev,
      {
        id: `link_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
        icNo: '',
        relationship: 'Anak'
      }
    ]);
  };

  const handleRemoveLinkChild = (index: number) => {
    if (regLinkChildren.length <= 1) {
      showToast('warning', 'Wajib Diisi', 'Pendaftaran sebagai Waris wajib memautkan sekurang-kurangnya seorang pesilat berdaftar.');
      return;
    }
    setRegLinkChildren(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateLinkChild = (index: number, field: 'icNo' | 'relationship', value: string) => {
    setRegLinkChildren(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Preload existing members for real-time verification in linking pesilat
  const [existingMembers, setExistingMembers] = useState<UserProfile[]>([]);

  useEffect(() => {
    getMembersDB().then(setExistingMembers).catch(() => {});
  }, []);

  const isPureWaris = regRole === 'Waris' && !regIsAlsoPesilat;
  const resolvedRole: Role = regRole === 'Waris'
    ? (regIsAlsoPesilat ? 'Ahli' : 'Waris')
    : 'Ahli';

  const handleGoogleAuth = async () => {
    setIsGoogleLoading(true);
    try {
      const user = await loginWithGoogle((existingMember, googleUser) => {
        setPendingGoogleBinding({ existingMember, googleUser });
      });
      if (user) {
        showToast('success', 'Log Masuk Berjaya', `Selamat datang ${user.name}`);
        onLoginSuccess();
      }
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request') {
        return;
      }
      if (error?.code === 'auth/popup-blocked') {
        showToast('error', 'Pop-Up Disekat', 'Sila benarkan pop-up untuk log masuk Google.');
        return;
      }
      showToast('error', 'Ralat Google Auth', error?.message || 'Gagal log masuk Google.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleConfirmBinding = async () => {
    if (!pendingGoogleBinding) return;
    setIsBindingLoading(true);
    try {
      const boundUser = await bindGoogleAccount(
        pendingGoogleBinding.existingMember,
        pendingGoogleBinding.googleUser
      );
      showToast('success', 'Akaun Dipautkan', `Selamat kembali, ${boundUser.name}`);
      setPendingGoogleBinding(null);
      onLoginSuccess();
    } catch (err: any) {
      showToast('error', 'Ralat Pautan', 'Gagal memautkan akaun Google.');
    } finally {
      setIsBindingLoading(false);
    }
  };

  const handleManualLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!icNo.trim()) {
      showToast('error', 'Sila masukkan No. IC');
      return;
    }

    setIsLoading(true);
    try {
      const cleanInput = sanitizeIc(icNo);
      const members = await getMembersDB();

      // Look up member by IC or ID
      const targetMember = members.find(
        (m) =>
          (m.icNo && sanitizeIc(m.icNo) === cleanInput) ||
          m.id.toLowerCase() === cleanInput.toLowerCase()
      );

      if (!targetMember) {
        showToast('error', 'Pengguna Tidak Ditemui', 'No. IC belum didaftarkan.');
        setIsLoading(false);
        return;
      }

      // Check status
      if (targetMember.status === 'Ditolak') {
        showToast('error', 'Akaun Ditolak', 'Pendaftaran anda tidak diluluskan. Sila hubungi pihak persatuan.');
        setIsLoading(false);
        return;
      }

      // Password verification
      if (targetMember.password) {
        if (!password) {
          showToast('error', 'Kata Laluan Diperlukan', 'Akaun ini dilindungi kata laluan. Sila masukkan kata laluan anda.');
          setIsLoading(false);
          return;
        }
        if (targetMember.password !== password) {
          showToast('error', 'Kata Laluan Salah', 'Sila semak semula kata laluan.');
          setIsLoading(false);
          return;
        }
      }

      // Proceed to login
      setCurrentUser(targetMember);
      showToast('success', 'Log Masuk Berjaya', `Selamat datang, ${targetMember.name}!`);
      onLoginSuccess();
    } catch {
      showToast('error', 'Ralat Sistem', 'Sila cuba sebentar lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePublicRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!regName.trim() || !regIcNo.trim() || !regAddress.trim()) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Sila isi Nama, No. IC dan Alamat Kediaman.');
      return;
    }

    const cleanIc = sanitizeIc(regIcNo);
    const cleanPhone = sanitizePhone(regPhone);
    const cleanEmail = regEmail.trim().toLowerCase();

    if (!cleanIc) {
      showToast('error', 'No. IC Tidak Sah', 'Sila masukkan format No. Kad Pengenalan yang sah.');
      return;
    }

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila semak semula alamat emel.');
      return;
    }

    // MANDATORY: Validation for Pesilat if registering as Waris
    if (regRole === 'Waris') {
      if (regWarisMode === 'link') {
        if (!regLinkChildren || regLinkChildren.length === 0) {
          showToast('error', 'Maklumat Pesilat Diperlukan', 'Pendaftaran sebagai Waris wajib memautkan sekurang-kurangnya seorang pesilat berdaftar.');
          return;
        }

        for (let i = 0; i < regLinkChildren.length; i++) {
          const l = regLinkChildren[i];
          const childLabel = regLinkChildren.length > 1 ? `Pesilat #${i + 1}` : 'Pesilat';
          const cleanChildIc = sanitizeIc(l.icNo);
          if (!cleanChildIc) {
            showToast('error', 'No. IC / MyKid Diperlukan', `Sila masukkan No. Kad Pengenalan / MyKid yang sah untuk ${childLabel}.`);
            return;
          }
          if (cleanChildIc === cleanIc) {
            showToast('error', 'No. IC Bertindih', `No. Kad Pengenalan untuk ${childLabel} tidak boleh sama dengan No. IC Waris.`);
            return;
          }
        }

        // Check duplicate ICs within regLinkChildren list
        const linkIcs = regLinkChildren.map(l => sanitizeIc(l.icNo));
        const seenLinkIcs = new Set<string>();
        for (const lIc of linkIcs) {
          if (seenLinkIcs.has(lIc)) {
            showToast('error', 'No. IC Pesilat Bertindih', `Terdapat No. IC yang sama berulang dalam senarai pautan (${lIc}).`);
            return;
          }
          seenLinkIcs.add(lIc);
        }
      } else {
        if (!regChildren || regChildren.length === 0) {
          showToast('error', 'Maklumat Pesilat Diperlukan', 'Pendaftaran sebagai Waris wajib menyertakan sekurang-kurangnya seorang pesilat berdaftar.');
          return;
        }

        for (let i = 0; i < regChildren.length; i++) {
          const c = regChildren[i];
          const childLabel = regChildren.length > 1 ? `Pesilat #${i + 1}` : 'Pesilat';
          if (!c.name.trim()) {
            showToast('error', 'Nama Pesilat Diperlukan', `Sila masukkan nama penuh untuk ${childLabel}.`);
            return;
          }
          const cleanChildIc = sanitizeIc(c.icNo);
          if (!cleanChildIc) {
            showToast('error', 'No. IC / MyKid Pesilat Tidak Sah', `Sila masukkan No. Kad Pengenalan / MyKid yang sah untuk ${childLabel}.`);
            return;
          }
          if (cleanChildIc === cleanIc) {
            showToast('error', 'No. IC Bertindih', `No. Kad Pengenalan untuk ${childLabel} tidak boleh sama dengan No. IC Waris.`);
            return;
          }
        }

        // Check duplicate ICs within regChildren list
        const childIcs = regChildren.map(c => sanitizeIc(c.icNo));
        const seenIcs = new Set<string>();
        for (const cIc of childIcs) {
          if (seenIcs.has(cIc)) {
            showToast('error', 'No. IC Pesilat Bertindih', 'Setiap pesilat yang didaftarkan mesti mempunyai No. Kad Pengenalan / MyKid berbeza.');
            return;
          }
          seenIcs.add(cIc);
        }

        // Check duplicate phones within regChildren list
        const seenChildPhones = new Set<string>();
        for (let i = 0; i < regChildren.length; i++) {
          const c = regChildren[i];
          const childCleanPhone = sanitizePhone(c.phone);
          const childLabel = regChildren.length > 1 ? `Pesilat #${i + 1} (${c.name})` : `Pesilat (${c.name})`;
          if (childCleanPhone) {
            if (cleanPhone && childCleanPhone === cleanPhone) {
              showToast('error', 'No. Telefon Bertindih', `No. telefon untuk ${childLabel} tidak boleh sama dengan No. telefon waris.`);
              return;
            }
            if (seenChildPhones.has(childCleanPhone)) {
              showToast('error', 'No. Telefon Pesilat Bertindih', `Setiap pesilat yang didaftarkan mesti mempunyai No. telefon yang unik.`);
              return;
            }
            seenChildPhones.add(childCleanPhone);
          }
        }
      }
    }

    setIsLoading(true);
    try {
      const existing = await getMembersDB();

      // Check for duplicate IC for Waris/Ahli
      const duplicateIc = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanIc);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Didaftar', `No. Kad Pengenalan ini sudah wujud dalam sistem atas nama ${duplicateIc.name}.`);
        setIsLoading(false);
        return;
      }

      // Check for duplicate phone for Waris/Ahli
      if (cleanPhone) {
        const duplicatePhone = existing.find(m => m.phone && sanitizePhone(m.phone) === cleanPhone);
        if (duplicatePhone) {
          showToast('error', 'No. Telefon Telah Didaftar', `No. telefon ${regPhone} sudah didaftarkan dalam sistem atas nama ${duplicatePhone.name}.`);
          setIsLoading(false);
          return;
        }
      }

      // Check if email already exists in database - FOR SECURITY: NEVER ALLOW SAME EMAIL IN MANUAL REGISTRATION
      const existingEmailMatch = cleanEmail ? existing.find(m => m.email && m.email.toLowerCase().trim() === cleanEmail) : null;
      if (existingEmailMatch) {
        showToast('error', 'Emel Telah Digunakan', 'Alamat emel ini sudah didaftarkan.');
        setIsLoading(false);
        return;
      }

      // If Waris, check each child/family member against existing database
      if (regRole === 'Waris') {
        if (regWarisMode === 'link') {
          for (let i = 0; i < regLinkChildren.length; i++) {
            const l = regLinkChildren[i];
            const cleanChildIc = sanitizeIc(l.icNo);
            const childLabel = regLinkChildren.length > 1 ? `Pesilat #${i + 1} (${cleanChildIc})` : `Pesilat (${cleanChildIc})`;

            const child = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanChildIc);
            if (!child) {
              showToast(
                'error',
                'Pesilat Tidak Ditemui',
                `${childLabel} belum wujud dalam pangkalan data. Sila pastikan No. IC betul atau gunakan tab "1. Daftar Pesilat Baharu".`
              );
              setIsLoading(false);
              return;
            }

            if (child.parentId && child.parentId.trim() && child.parentId.trim() !== cleanIc) {
              showToast('error', 'Pesilat Sudah Dipautkan', `Pesilat ${child.name} (${cleanChildIc}) sudah dipautkan ke akaun waris lain.`);
              setIsLoading(false);
              return;
            }
          }
        } else {
          for (let i = 0; i < regChildren.length; i++) {
            const c = regChildren[i];
            const cleanChildIc = sanitizeIc(c.icNo);
            const cleanChildPhone = sanitizePhone(c.phone);
            const childLabel = regChildren.length > 1 ? `Pesilat #${i + 1} (${c.name})` : `Pesilat (${c.name})`;

            const duplicateChild = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanChildIc);
            if (duplicateChild) {
              if (duplicateChild.parentId && duplicateChild.parentId.trim() && duplicateChild.parentId.trim() !== cleanIc) {
                showToast('error', 'Pesilat Sudah Berdaftar', `${childLabel} sudah pun berdaftar di bawah akaun waris lain dalam pangkalan data.`);
                setIsLoading(false);
                return;
              }
            }

            if (cleanChildPhone) {
              const duplicateChildPhone = existing.find(m => m.id !== duplicateChild?.id && m.phone && sanitizePhone(m.phone) === cleanChildPhone);
              if (duplicateChildPhone) {
                showToast('error', 'No. Telefon Pesilat Telah Didaftar', `No. telefon ${cleanChildPhone} bagi ${childLabel} sudah didaftarkan atas nama ${duplicateChildPhone.name}.`);
                setIsLoading(false);
                return;
              }
            }
          }
        }
      }

      // Find current highest numerical ID for sequential assignment
      let maxNum = 0;
      for (const m of existing) {
        const match = m.id.match(/^asmm-(\d+)$/i);
        if (match) {
          const num = parseInt(match[1], 10);
          if (num > maxNum) maxNum = num;
        }
      }

      // 1. Fresh new registration for Waris / Ahli
      maxNum += 1;
      const primaryId = `ASMM-${String(maxNum).padStart(3, '0')}`;
      const newMember: UserProfile = {
        id: primaryId,
        name: toTitleCase(regName.trim()),
        icNo: cleanIc,
        email: cleanEmail || `${cleanIc}@asmm.org.my`,
        phone: cleanPhone || '',
        address: regAddress.trim(),
        role: resolvedRole,
        roles: regRole === 'Waris'
          ? (regIsAlsoPesilat ? ['Ahli', 'Waris'] : ['Waris'])
          : ['Ahli'],
        isAlsoPesilat: regRole === 'Waris' ? regIsAlsoPesilat : true,
        gelanggang: isPureWaris ? '' : regGelanggang,
        bengkung: isPureWaris ? ('' as BengkungLevel) : ('Putih' as BengkungLevel),
        bengkungBadge: isPureWaris ? '' : '/badges/putih0.png',
        photoUrl: '/default-avatar.svg',
        joinedDate: new Date().toISOString().split('T')[0],
        status: isPureWaris ? ('Aktif' as const) : ('Menunggu Pengesahan' as const),
        password: regPassword,
        sessionTime: isPureWaris ? '' : 'Semua Sesi',
        authProvider: 'password' as const,
        isOldMember: false,
        onboardingStatus: isPureWaris ? ('completed' as const) : ('pending_payment' as const),
        uniformSize: isPureWaris ? '' : regUniformSize
      };

      if (!isPureWaris) {
        const onbInv = createOnboardingInvoice(newMember, regUniformSize);
        await saveInvoiceDB(onbInv);
        newMember.onboardingInvoiceId = onbInv.id;
      }

      await saveMemberDB(newMember);

      // 2. If Waris, register or link all mandatory pesilat
      const savedChildNames: string[] = [];
      if (regRole === 'Waris') {
        if (regWarisMode === 'link') {
          for (const item of regLinkChildren) {
            const cleanChildIc = sanitizeIc(item.icNo);
            const existingChild = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanChildIc);

            if (existingChild) {
              const updatedChild: UserProfile = {
                ...existingChild,
                parentId: primaryId,
                relationship: item.relationship || 'Tanggungan',
                kinName: newMember.name,
                kinPhone: newMember.phone
              };
              await saveMemberDB(updatedChild);
              savedChildNames.push(existingChild.name);
            }
          }
        } else {
          for (const child of regChildren) {
            const cleanChildIc = sanitizeIc(child.icNo);
            const existingChild = existing.find(m => m.icNo && sanitizeIc(m.icNo) === cleanChildIc);

            if (existingChild) {
              // Profile was already created (e.g. by coach); link to this Waris
              const updatedChild: UserProfile = {
                ...existingChild,
                name: toTitleCase(existingChild.name),
                parentId: primaryId,
                relationship: child.relationship || 'Tanggungan',
                kinName: newMember.name,
                kinPhone: newMember.phone
              };
              await saveMemberDB(updatedChild);
              savedChildNames.push(existingChild.name);
            } else {
              // Fresh new member profile
              maxNum += 1;
              const childId = `ASMM-${String(maxNum).padStart(3, '0')}`;
              const childCleanEmail = `${cleanChildIc}@asmm.org.my`;
              const childCleanPhone = sanitizePhone(child.phone || '') || '';

              const newChild: UserProfile = {
                id: childId,
                name: toTitleCase(child.name.trim()),
                icNo: cleanChildIc,
                email: childCleanEmail,
                phone: childCleanPhone,
                address: regAddress.trim(),
                role: 'Ahli',
                roles: ['Ahli'],
                gelanggang: child.gelanggang,
                bengkung: 'Putih' as BengkungLevel,
                bengkungBadge: '/badges/putih0.png',
                photoUrl: '/default-avatar.svg',
                joinedDate: new Date().toISOString().split('T')[0],
                status: 'Menunggu Pengesahan',
                parentId: primaryId,
                relationship: child.relationship || 'Tanggungan',
                password: cleanChildIc,
                sessionTime: 'Semua Sesi',
                authProvider: 'password',
                isOldMember: false,
                onboardingStatus: 'pending_payment',
                uniformSize: child.uniformSize,
                kinName: newMember.name,
                kinPhone: newMember.phone
              };

              const childOnbInv = createOnboardingInvoice(newChild, child.uniformSize);
              await saveInvoiceDB(childOnbInv);
              newChild.onboardingInvoiceId = childOnbInv.id;

              await saveMemberDB(newChild);
              savedChildNames.push(newChild.name);
            }
          }
        }
      }

      window.dispatchEvent(new Event('asmm_db_updated'));

      if (isPureWaris) {
        showToast(
          'success',
          'Pendaftaran Waris Berjaya!',
          regWarisMode === 'link'
            ? `Akaun Waris ${regName} aktif dan ${savedChildNames.length} pesilat (${savedChildNames.join(', ')}) berjaya dipautkan.`
            : `Akaun Waris ${regName} aktif dan ${savedChildNames.length} pesilat berjaya didaftarkan. Sila teruskan ke menu Bayaran.`
        );
      } else {
        showToast(
          'success',
          'Pendaftaran Berjaya!',
          'Sila teruskan ke menu Bayaran untuk melengkapkan yuran pendaftaran keahlian & uniform.'
        );
      }

      // Automatically authenticate the newly registered member so they proceed straight to bayar
      setCurrentUser(newMember);
      onLoginSuccess();
    } catch {
      showToast('error', 'Ralat Pendaftaran', 'Gagal menyimpan rekod.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Fixed Header Banner */}
      <PageHeader
        badgeText="Portal Akses Ahli"
        badgeIcon={ShieldCheck}
        title="Log Masuk & Pendaftaran"
      />

      {/* Main Grid */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden grid grid-cols-1 lg:grid-cols-12">
        {/* Left Column: Organization Heritage Banner (Desktop only - Hidden on mobile for simple, instant mobile login) */}
        <div className="hidden lg:flex lg:col-span-7 bg-slate-900 text-white p-6 sm:p-10 flex-col justify-between space-y-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-asmm-red/10 rounded-full blur-3xl pointer-events-none" />

          <div className="space-y-4 relative z-10">
            <div className="flex items-center gap-3">
              <img src="/logo-pssasmm.png" alt="ASMM Logo" className="h-12 w-auto object-contain" loading="lazy" decoding="async" />
              <div>
                <span className="text-[10px] text-amber-400 font-extrabold tracking-widest uppercase block">
                  Portal Rasmi PSSASMM (PPM-007-02-28082022)
                </span>
                <h2 className="text-xl font-extrabold tracking-tight text-white leading-tight">
                  Seni Silat Awang Sulung Merah Muda
                </h2>
              </div>
            </div>
          </div>

          <div className="space-y-2 relative z-10">
            {/* Feature 1: Dashboard & Bengkung */}
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-colors">
              <Award className="w-5 h-5 text-amber-400 shrink-0" />
              <h4 className="text-xs font-bold text-white">Dashboard Profil & Status Bengkung</h4>
            </div>

            {/* Feature 2: Yuran & FPX */}
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-colors">
              <CreditCard className="w-5 h-5 text-emerald-400 shrink-0" />
              <h4 className="text-xs font-bold text-white">Pembayaran Yuran Gelanggang & Ujian</h4>
            </div>

            {/* Feature 3: Notis & RSVP */}
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-colors">
              <Bell className="w-5 h-5 text-rose-400 shrink-0" />
              <h4 className="text-xs font-bold text-white">Notis Rasmi & RSVP Kehadiran</h4>
            </div>

            {/* Feature 4: Silibus & Video */}
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-center gap-3 hover:bg-white/10 transition-colors">
              <BookOpen className="w-5 h-5 text-sky-400 shrink-0" />
              <h4 className="text-xs font-bold text-white">Silibus & Sukatan Pelajaran</h4>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-800 text-[11px] text-slate-400 italic flex items-center justify-between">
            <span>"Biduk berlalu kiambang bertaut"</span>
          </div>
        </div>

        {/* Right Column / Mobile Main: Switchable Form (5 Cols on Desktop, Full Width on Mobile) */}
        <div className="lg:col-span-5 p-5 sm:p-8 bg-white flex flex-col justify-between space-y-6">
          <div>
            <div className="grid grid-cols-2 p-1 bg-slate-100 rounded-2xl border border-slate-200 mb-6">
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  if (onModeChange) onModeChange('login');
                }}
                className={`py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${mode === 'login'
                  ? 'bg-white text-asmm-red shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <LogIn className="w-3.5 h-3.5" />
                <span>Log Masuk</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  if (onModeChange) onModeChange('register');
                }}
                className={`py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${mode === 'register'
                  ? 'bg-white text-asmm-red shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <UserPlus className="w-3.5 h-3.5" />
                <span>Daftar Ahli</span>
              </button>
            </div>

            {mode === 'login' ? (
              <>
                {(() => {
                  const intended = typeof window !== 'undefined' ? sessionStorage.getItem('asmm_intended_url') : null;
                  if (!intended) return null;
                  const isCalendar = intended.includes('calendar') || intended.includes('gelanggang');
                  return (
                    <div className="mb-4 p-3.5 bg-teal-50 border border-teal-200 rounded-2xl flex items-center gap-3 text-teal-950 text-xs animate-fade-in shadow-2xs">
                      <div className="p-2 bg-teal-600 text-white rounded-xl shrink-0">
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <span className="block font-black text-teal-950">Pautan Dikongsi Dikesan</span>
                        <span className="block text-[11px] text-teal-700 font-medium">
                          {isCalendar
                            ? 'Sila log masuk untuk terus ke jadual ketersediaan gelanggang yang anda ingin lawati.'
                            : 'Sila log masuk untuk terus ke halaman yang anda ingin lawati.'}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                <div className="mb-4">
                  <h3 className="text-base font-extrabold text-slate-900">Akses Portal Ahli</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Pilih kaedah log masuk Google atau No. IC berdaftar</p>
                </div>

                {/* Google Sign In Button */}
                <button
                  type="button"
                  onClick={handleGoogleAuth}
                  disabled={isGoogleLoading || isLoading}
                  className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl text-slate-700 font-bold text-xs shadow-xs transition-all flex items-center justify-center gap-2.5 disabled:opacity-60 cursor-pointer mb-4"
                >
                  {isGoogleLoading ? (
                    <span className="flex items-center gap-2 text-slate-600">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Menghubungkan Google...
                    </span>
                  ) : (
                    <>
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z" />
                        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.34 24 12 24z" />
                        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
                        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                      </svg>
                      <span>Log Masuk dengan Google</span>
                    </>
                  )}
                </button>

                <div className="flex items-center gap-3 my-4">
                  <div className="h-px bg-slate-200 flex-1"></div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">atau No. IC</span>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>

                {/* Manual Login Form */}
                <form onSubmit={handleManualLogin} className="space-y-3.5 mb-6">

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      No. IC / Emel Ahli
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={icNo}
                        onChange={(e) => setIcNo(e.target.value)}
                        placeholder="Contoh: 850612-02-5431"
                        className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Kata Laluan
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isLoading ? (
                      <span>Menyemak Pangkalan Data...</span>
                    ) : (
                      <>
                        <span>Masuk Ke Portal</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </form>
              </>
            ) : (
              /* Public Registration Form */
              <div className="space-y-4">
                <div className="mb-3">
                  <h3 className="text-base font-extrabold text-slate-900">
                    {regRole === 'Waris' ? 'Pendaftaran Akaun Waris' : 'Pendaftaran Pesilat Baharu'}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {regRole === 'Waris'
                      ? 'Daftar sebagai ibu bapa / waris / penjaga untuk menguruskan yuran dan kemajuan ahli keluarga.'
                      : 'Daftar pantas dengan akaun Google atau lengkapkan borang pendaftaran rasmi'}
                  </p>
                </div>

                {/* Google Quick Sign-Up */}
                <button
                  type="button"
                  onClick={handleGoogleAuth}
                  disabled={isGoogleLoading || isLoading}
                  className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 border border-slate-300 rounded-xl text-slate-700 font-bold text-xs shadow-xs transition-all flex items-center justify-center gap-2.5 disabled:opacity-60 cursor-pointer mb-2"
                >
                  {isGoogleLoading ? (
                    <span className="flex items-center gap-2 text-slate-600">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Mendaftar dengan Google...
                    </span>
                  ) : (
                    <>
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z" />
                        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.34 24 12 24z" />
                        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
                        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                      </svg>
                      <span>Daftar Pantas dengan Google</span>
                    </>
                  )}
                </button>

                <div className="flex items-center gap-3 my-3">
                  <div className="h-px bg-slate-200 flex-1"></div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">atau borang manual</span>
                  <div className="h-px bg-slate-200 flex-1"></div>
                </div>

                <form onSubmit={handlePublicRegistration} className="space-y-3.5">


                  {/* Role Switcher */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Jenis Pendaftaran *</label>
                    <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl border border-slate-200">
                      <button
                        type="button"
                        onClick={() => { setRegRole('Ahli'); setRegIsAlsoPesilat(false); }}
                        className={`py-1.5 rounded-lg text-xs font-bold transition-all ${regRole === 'Ahli'
                          ? 'bg-white text-asmm-red shadow-xs font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        Pesilat (Ahli)
                      </button>
                      <button
                        type="button"
                        onClick={() => setRegRole('Waris')}
                        className={`py-1.5 rounded-lg text-xs font-bold transition-all ${regRole === 'Waris'
                          ? 'bg-white text-asmm-red shadow-xs font-bold'
                          : 'text-slate-600 hover:text-slate-900'
                          }`}
                      >
                        Waris (Ibu Bapa)
                      </button>
                    </div>
                  </div>

                  {/* Waris: Also a Pesilat? Toggle */}
                  {regRole === 'Waris' && (
                    <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl space-y-2">
                      <label className="block text-xs font-bold text-amber-800">Adakah anda juga seorang pesilat ASMM?</label>
                      <div className="grid grid-cols-2 gap-2 p-1 bg-white rounded-xl border border-amber-200">
                        <button
                          type="button"
                          onClick={() => setRegIsAlsoPesilat(false)}
                          className={`py-1.5 rounded-lg text-xs font-bold transition-all ${!regIsAlsoPesilat
                            ? 'bg-amber-100 text-amber-900 shadow-xs border border-amber-300'
                            : 'text-slate-600 hover:text-slate-900 border border-transparent'
                            }`}
                        >
                          Tidak, Waris Sahaja
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegIsAlsoPesilat(true)}
                          className={`py-1.5 rounded-lg text-xs font-bold transition-all ${regIsAlsoPesilat
                            ? 'bg-amber-100 text-amber-900 shadow-xs border border-amber-300'
                            : 'text-slate-600 hover:text-slate-900 border border-transparent'
                            }`}
                        >
                          Ya, Saya Juga Pesilat
                        </button>
                      </div>

                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      {regRole === 'Waris' ? 'Nama Penuh Waris (Ibu Bapa / Penjaga) *' : 'Nama Penuh Pesilat *'}
                    </label>
                    <input
                      type="text"
                      required
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      placeholder={regRole === 'Waris' ? "Contoh: Ahmad Zaki Bin Sulaiman" : "Contoh: Amirul Bin Ahmad Zaki"}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        {regRole === 'Waris' ? 'No. Kad Pengenalan Waris *' : 'No. Kad Pengenalan *'}
                      </label>
                      <input
                        type="text"
                        required
                        value={regIcNo}
                        onChange={(e) => setRegIcNo(e.target.value)}
                        placeholder="030819-02-5819"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        {regRole === 'Waris' ? 'No. Telefon Waris *' : 'No. Telefon (WhatsApp)'}
                      </label>
                      <input
                        type="text"
                        required={regRole === 'Waris'}
                        value={regPhone}
                        onChange={(e) => setRegPhone(e.target.value)}
                        placeholder="019-4920194"
                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      {regRole === 'Waris' ? 'Alamat Emel Waris' : 'Alamat Emel'}
                    </label>
                    <input
                      type="email"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      placeholder={regRole === 'Waris' ? "ahmadzaki@gmail.com" : "amirul@asmm.org.my"}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      {regRole === 'Waris' ? 'Alamat Kediaman Waris *' : 'Alamat Kediaman Pesilat *'}
                    </label>
                    <textarea
                      required
                      rows={2}
                      value={regAddress}
                      onChange={(e) => setRegAddress(e.target.value)}
                      placeholder="Contoh: No 12, Jalan Sejahtera 3, Taman Sejahtera, 09600 Lunas, Kedah"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none"
                    />
                  </div>

                  {/* Gelanggang — for Ahli or Waris who is also a pesilat */}
                  {!isPureWaris && (
                    <div className="space-y-3 p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl">
                      <div className="text-xs font-bold text-slate-800">
                        {regRole === 'Waris' ? 'Maklumat Latihan Waris (Sebagai Pesilat)' : 'Maklumat Latihan Pesilat'}
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">
                          {regRole === 'Waris' ? 'Gelanggang Latihan Waris' : 'Gelanggang Latihan Pilihan'}
                        </label>
                        <select
                          value={regGelanggang}
                          onChange={(e) => setRegGelanggang(e.target.value as Gelanggang)}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                        >
                          <option value="Pusaka Warisan">Pusaka Warisan</option>
                          <option value="Wadi AnNour">Wadi AnNour</option>
                          <option value="Paya Besar">Paya Besar</option>
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                            <span>{regRole === 'Waris' ? 'Saiz Uniform Silat Waris (Ketinggian) *' : 'Saiz Uniform Silat (Ketinggian) *'}</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => setIsSizeChartOpen(true)}
                            className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 text-xs font-bold cursor-pointer bg-transparent border-none p-0"
                            title="Buka Carta Cadangan Saiz"
                          >
                            <Ruler className="w-3.5 h-3.5" />
                            <span>Carta Saiz</span>
                          </button>
                        </div>
                        <select
                          value={regUniformSize}
                          onChange={(e) => setRegUniformSize(e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                        >
                          {UNIFORM_SIZES.map(s => (
                            <option key={s} value={s}>
                              {s} — RM {getUniformPriceBySize(s)} ({parseInt(s) <= 130 ? 'Kanak-kanak' : parseInt(s) <= 170 ? 'Remaja / Dewasa' : 'Tinggi / Dewasa'})
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Kadar: 110-130cm (RM120) • 140-170cm (RM130) • 180-200cm (RM140)
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Waris: Mandatory Pesilat Details */}
                  {regRole === 'Waris' && (
                    <div className="p-4 sm:p-5 bg-slate-50 border border-slate-200/90 rounded-2xl space-y-4">
                      <div className="flex items-start justify-between gap-3 border-b border-slate-200/80 pb-3.5">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-xl bg-asmm-red/10 text-asmm-red border border-asmm-red/20 flex items-center justify-center shrink-0 mt-0.5">
                            <Users className="w-4 h-4 text-asmm-red" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-xs sm:text-sm font-black text-slate-900">Maklumat Pesilat (Tanggungan / Ahli Keluarga)</h4>
                              <span className="text-[10px] font-black bg-asmm-red text-white px-2 py-0.5 rounded-full uppercase tracking-wider">
                                Wajib
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                              Pendaftaran akaun Waris wajib menyertakan maklumat pesilat (anak, isteri, suami, atau tanggungan) yang diselia di bawah akaun anda.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Mode Selector Tabs (Sleek Segmented Pill Control) */}
                      <div className="grid grid-cols-2 gap-1.5 p-1 bg-slate-200/70 rounded-xl border border-slate-200">
                        <button
                          type="button"
                          onClick={() => setRegWarisMode('register')}
                          className={`py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${regWarisMode === 'register'
                            ? 'bg-white text-slate-900 shadow-xs ring-1 ring-slate-900/5'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
                            }`}
                        >
                          <UserPlus className={`w-3.5 h-3.5 ${regWarisMode === 'register' ? 'text-asmm-red' : 'text-slate-400'}`} />
                          <span className="truncate">Daftar Pesilat Baharu</span>
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                            regWarisMode === 'register' ? 'bg-red-50 text-asmm-red' : 'bg-slate-300/60 text-slate-600'
                          }`}>
                            {regChildren.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegWarisMode('link')}
                          className={`py-2 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer border-none ${regWarisMode === 'link'
                            ? 'bg-white text-slate-900 shadow-xs ring-1 ring-slate-900/5'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-white/40'
                            }`}
                        >
                          <Link2 className={`w-3.5 h-3.5 ${regWarisMode === 'link' ? 'text-asmm-red' : 'text-slate-400'}`} />
                          <span className="truncate">Pautkan Pesilat Sedia Ada</span>
                          <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full ${
                            regWarisMode === 'link' ? 'bg-red-50 text-asmm-red' : 'bg-slate-300/60 text-slate-600'
                          }`}>
                            {regLinkChildren.length}
                          </span>
                        </button>
                      </div>

                      {/* MODE 1: DAFTAR PESILAT BAHARU */}
                      {regWarisMode === 'register' && (
                        <div className="space-y-3.5">
                          {/* Pesilat List */}
                          <div className="space-y-3.5">
                            {regChildren.map((child, idx) => (
                              <div key={idx} className="p-3.5 sm:p-4 bg-white rounded-xl border border-slate-200/90 shadow-2xs space-y-3.5">
                                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                                  <div className="flex items-center gap-2">
                                    <span className="w-5 h-5 rounded-md bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">
                                      {idx + 1}
                                    </span>
                                    <span className="text-xs font-bold text-slate-800">
                                      {regChildren.length > 1 ? `Pesilat #${idx + 1}` : 'Butiran Pesilat Baharu'}
                                    </span>
                                    {idx === 0 && (
                                      <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-md">
                                        Pesilat Utama
                                      </span>
                                    )}
                                  </div>

                                  {regChildren.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveChild(idx)}
                                      className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 transition-colors border-none bg-transparent cursor-pointer p-0"
                                      title="Padam maklumat pesilat ini"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      <span>Padam</span>
                                    </button>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                                  <div className="sm:col-span-8">
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                      Nama Penuh Pesilat *
                                    </label>
                                    <input
                                      type="text"
                                      required={regWarisMode === 'register'}
                                      value={child.name}
                                      onChange={(e) => handleUpdateChild(idx, 'name', e.target.value)}
                                      placeholder="Contoh: Muhammad Adam Bin Ahmad"
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    />
                                  </div>

                                  <div className="sm:col-span-4">
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                      Hubungan *
                                    </label>
                                    <select
                                      value={child.relationship || 'Anak'}
                                      onChange={(e) => handleUpdateChild(idx, 'relationship', e.target.value)}
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    >
                                      <option value="Anak">Anak</option>
                                      <option value="Isteri">Isteri</option>
                                      <option value="Suami">Suami</option>
                                      <option value="Adik-beradik">Adik-beradik</option>
                                      <option value="Ibu">Ibu</option>
                                      <option value="Bapa">Bapa</option>
                                      <option value="Tanggungan Lain">Tanggungan Lain</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                                  <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                      No. Kad Pengenalan / MyKid *
                                    </label>
                                    <input
                                      type="text"
                                      required={regWarisMode === 'register'}
                                      value={child.icNo}
                                      onChange={(e) => handleUpdateChild(idx, 'icNo', e.target.value)}
                                      placeholder="Contoh: 120504-02-1234"
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                      Gelanggang Latihan *
                                    </label>
                                    <select
                                      value={child.gelanggang}
                                      onChange={(e) => handleUpdateChild(idx, 'gelanggang', e.target.value as Gelanggang)}
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    >
                                      <option value="Pusaka Warisan">Pusaka Warisan</option>
                                      <option value="Wadi AnNour">Wadi AnNour</option>
                                      <option value="Paya Besar">Paya Besar</option>
                                    </select>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
                                  <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                                        <span>Saiz Uniform Silat Pesilat *</span>
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => setIsSizeChartOpen(true)}
                                        className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1 text-[11px] font-bold cursor-pointer bg-transparent border-none p-0"
                                        title="Buka Carta Cadangan Saiz"
                                      >
                                        <Ruler className="w-3 h-3" />
                                        <span>Carta Saiz</span>
                                      </button>
                                    </div>
                                    <select
                                      value={child.uniformSize}
                                      onChange={(e) => handleUpdateChild(idx, 'uniformSize', e.target.value)}
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    >
                                      {UNIFORM_SIZES.map(s => (
                                        <option key={s} value={s}>
                                          {s} — RM {getUniformPriceBySize(s)} ({parseInt(s) <= 130 ? 'Kanak-kanak' : parseInt(s) <= 170 ? 'Remaja / Dewasa' : 'Tinggi / Dewasa'})
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                      No. Telefon Pesilat (Pilihan)
                                    </label>
                                    <input
                                      type="text"
                                      value={child.phone || ''}
                                      onChange={(e) => handleUpdateChild(idx, 'phone', e.target.value)}
                                      placeholder="Kosongkan jika guna nombor waris"
                                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Add Another Pesilat Button */}
                          <button
                            type="button"
                            onClick={handleAddChild}
                            className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 rounded-xl border border-slate-200/90 hover:border-slate-300 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs active:scale-[0.99]"
                          >
                            <Plus className="w-4 h-4 text-asmm-red" />
                            <span>Tambah Pesilat Baharu Lagi</span>
                          </button>
                        </div>
                      )}

                      {/* MODE 2: PAUTKAN PESILAT SEDIA ADA */}
                      {regWarisMode === 'link' && (
                        <div className="space-y-3.5">
                          <div className="p-3 bg-blue-50/60 border border-blue-200/60 rounded-xl flex items-start gap-2.5">
                            <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                            <div className="text-xs text-slate-600 leading-relaxed">
                              <strong className="font-bold text-slate-800">Pesilat sudah berdaftar sebelum ini?</strong> Masukkan No. Kad Pengenalan atau MyKid untuk memautkan akaun pesilat terus ke profil Waris anda tanpa perlu membeli set uniform baru.
                            </div>
                          </div>

                          <div className="space-y-3">
                            {regLinkChildren.map((item, idx) => {
                              const cleanItemIc = sanitizeIc(item.icNo);
                              const matchedMember = cleanItemIc && cleanItemIc.length >= 6
                                ? existingMembers.find(m => m.icNo && sanitizeIc(m.icNo) === cleanItemIc)
                                : null;

                              return (
                                <div key={item.id} className="p-3.5 sm:p-4 bg-white rounded-xl border border-slate-200/90 shadow-2xs space-y-3.5">
                                  <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                                    <div className="flex items-center gap-2">
                                      <span className="w-5 h-5 rounded-md bg-slate-900 text-white text-[10px] font-black flex items-center justify-center">
                                        {idx + 1}
                                      </span>
                                      <span className="text-xs font-bold text-slate-800">
                                        {regLinkChildren.length > 1 ? `Pautan Pesilat #${idx + 1}` : 'Maklumat Pesilat Sedia Ada'}
                                      </span>
                                    </div>
                                    {regLinkChildren.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveLinkChild(idx)}
                                        className="text-xs text-red-600 hover:text-red-700 font-bold flex items-center gap-1 border-none bg-transparent cursor-pointer p-0"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        <span>Padam</span>
                                      </button>
                                    )}
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start">
                                    <div className="sm:col-span-8">
                                      <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                        No. Kad Pengenalan / MyKid Pesilat *
                                      </label>
                                      <input
                                        type="text"
                                        required={regWarisMode === 'link'}
                                        value={item.icNo}
                                        onChange={(e) => handleUpdateLinkChild(idx, 'icNo', e.target.value)}
                                        placeholder="Contoh: 120504-02-1234"
                                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                      />
                                    </div>

                                    <div className="sm:col-span-4">
                                      <label className="block text-xs font-bold text-slate-700 mb-1.5">
                                        Hubungan *
                                      </label>
                                      <select
                                        value={item.relationship}
                                        onChange={(e) => handleUpdateLinkChild(idx, 'relationship', e.target.value)}
                                        className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium transition-all"
                                      >
                                        <option value="Anak">Anak</option>
                                        <option value="Isteri">Isteri</option>
                                        <option value="Suami">Suami</option>
                                        <option value="Adik-beradik">Adik-beradik</option>
                                        <option value="Ibu">Ibu</option>
                                        <option value="Bapa">Bapa</option>
                                        <option value="Tanggungan Lain">Tanggungan Lain</option>
                                      </select>
                                    </div>
                                  </div>

                                  {/* Real-time Match Feedback */}
                                  {cleanItemIc && cleanItemIc.length >= 6 && (
                                    <div className="pt-0.5">
                                      {matchedMember ? (
                                        <div className="p-2.5 bg-emerald-50/80 border border-emerald-200/90 rounded-xl flex items-center justify-between gap-2 text-xs">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                            <div className="truncate">
                                              <span className="font-extrabold text-emerald-950">{matchedMember.name}</span>
                                              <span className="text-[11px] text-emerald-700 font-medium ml-1.5">• Gelanggang {matchedMember.gelanggang || 'ASMM'} ({matchedMember.bengkung || 'Pesilat'})</span>
                                            </div>
                                          </div>
                                          {matchedMember.parentId ? (
                                            <span className="text-[10px] font-bold text-amber-800 bg-amber-100/90 px-2 py-0.5 rounded-full shrink-0">
                                              Sudah Berwaris
                                            </span>
                                          ) : (
                                            <span className="text-[10px] font-bold text-emerald-800 bg-emerald-100/90 px-2 py-0.5 rounded-full shrink-0">
                                              Sedia Dipautkan
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="p-2.5 bg-amber-50/70 border border-amber-200/70 rounded-xl flex items-start gap-2 text-[11px] text-amber-900">
                                          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                          <span>No. IC belum wujud dalam rekod ASMM. Jika belum pernah mendaftar, sila tukar ke tab <strong>"Daftar Pesilat Baharu"</strong>.</span>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <button
                            type="button"
                            onClick={handleAddLinkChild}
                            className="w-full py-2.5 px-4 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 rounded-xl border border-slate-200/90 hover:border-slate-300 text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-2xs active:scale-[0.99]"
                          >
                            <Plus className="w-4 h-4 text-asmm-red" />
                            <span>Pautkan Pesilat Sedia Ada Lagi</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Kata Laluan Baru *</label>
                    <input
                      type="password"
                      required
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      placeholder="Kata Laluan Minimum 6 Aksara"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3.5 bg-asmm-red hover:bg-asmm-crimson text-white font-bold text-xs rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 border-none cursor-pointer"
                  >
                    {isLoading ? (
                      <span>Menyimpan Rekod...</span>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>
                          {regRole === 'Waris'
                            ? (regWarisMode === 'link'
                              ? 'Daftar Akaun Waris & Pautkan Pesilat'
                              : (isPureWaris ? 'Daftar Akaun Waris & Pesilat' : 'Daftar Keahlian Waris & Pesilat'))
                            : 'Hantar & Aktifkan Keahlian'}
                        </span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Modal Pengesahan Pautan Akaun Google */}
      <Modal
        isOpen={!!pendingGoogleBinding}
        onClose={() => {
          if (!isBindingLoading) {
            setPendingGoogleBinding(null);
            showToast('info', 'Pautan Akaun Dibatalkan', 'Anda boleh log masuk menggunakan No. IC atau daftar dengan akaun lain.');
          }
        }}
        title="Pautkan Akaun Google"
        maxWidth="max-w-md"
      >
        {pendingGoogleBinding && (
          <div className="space-y-5">
            {/* Notice header */}
            <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-xs font-bold text-amber-900">Emel Sedia Ada Ditemui</h4>
                <p className="text-[11px] text-amber-800 mt-0.5 leading-relaxed">
                  Emel Google <strong className="font-semibold text-amber-950">{pendingGoogleBinding.googleUser.email}</strong> telah berdaftar dalam pangkalan data ASMM.
                </p>
              </div>
            </div>

            {/* Profile match card */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Profil Sepadan Dalam Sistem
              </div>
              <div className="flex items-center gap-3">
                <img
                  src={pendingGoogleBinding.existingMember.photoUrl || '/default-avatar.svg'}
                  alt={pendingGoogleBinding.existingMember.name}
                  onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                  className="w-12 h-12 rounded-xl object-cover border border-slate-200 shadow-xs"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-slate-900 truncate">
                    {pendingGoogleBinding.existingMember.name}
                  </h4>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-red-50 text-asmm-red rounded-md">
                      {pendingGoogleBinding.existingMember.role}
                    </span>
                    <span className="text-[10px] text-slate-500 truncate">
                      {pendingGoogleBinding.existingMember.gelanggang}
                    </span>
                  </div>
                  {pendingGoogleBinding.existingMember.icNo && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      No. IC: <span className="font-mono font-medium text-slate-700">{pendingGoogleBinding.existingMember.icNo}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Question description */}
            <p className="text-xs text-slate-600 leading-relaxed text-center">
              Adakah anda ingin <strong>menghubungkan (bind)</strong> akaun Google ini kepada profil di atas supaya anda boleh log masuk serta-merta pada masa hadapan?
            </p>

            {/* Action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              <button
                type="button"
                onClick={() => setPendingGoogleBinding(null)}
                disabled={isBindingLoading}
                className="w-full py-2.5 px-3 rounded-xl border border-slate-300 text-slate-700 text-xs font-bold hover:bg-slate-100 transition-colors disabled:opacity-50 cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleConfirmBinding}
                disabled={isBindingLoading}
                className="w-full py-2.5 px-3 rounded-xl bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold transition-all shadow-xs flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer"
              >
                {isBindingLoading ? (
                  <span>Memautkan Akaun...</span>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    <span>Hubungkan & Masuk</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Uniform Size Chart Modal Popup */}
      <UniformSizeChartModal
        isOpen={isSizeChartOpen}
        onClose={() => setIsSizeChartOpen(false)}
        selectedSize={regUniformSize}
        onSelectSize={(s) => setRegUniformSize(s)}
      />
    </div>
  );
};
